// src/services/api-dtime/api_incorporate.js
// ⭐ อัปเดต — ย้ายจาก httpClientImmigration (immigration service, port 2088)
// ไปใช้ httpClientInCorporate (new_dtime_corporate_service_node_backend, port 2089)
// เพราะ Incorporate & Corporate module แยกเป็น service อิสระของตัวเองแล้ว

import httpClientInCorporate from "@/services/httpClientInCorporate";
import { server } from "@/services/constants";

// ============================================================
// INCORPORATE MATTER
// ============================================================

/**
 * ดึงข้อมูล matter ทั้งหมด (ไม่มี pagination)
 */
export const getAllIncorporateMatter = () => {
  return httpClientInCorporate.get(`${server.INCORPORATE_MATTER}`);
};

/**
 * ดึงข้อมูล matter แบบ pagination + search + filter
 * @param {number} page
 * @param {number} limit
 * @param {string} sortBy
 * @param {string} sortOrder  ASC | DESC
 * @param {string|number} company_id
 * @param {string} search     ค้นหาจาก client_name, registration_no, nationality, company_name
 * @param {string} entity_type Limited Company | Public Company Limited | Foundation | Representative Office | Individual
 * @param {string} status     Active | Inactive
 */
export const getIncorporateMatterPagination = (
  page,
  limit,
  sortBy,
  sortOrder,
  company_id,
  search,
  entity_type,
  status,
) => {
  return httpClientInCorporate.get(`${server.INCORPORATE_MATTER_PAGINATION}`, {
    params: {
      page,
      limit,
      sortBy,
      sortOrder,
      company_id,
      search,
      entity_type,
      status,
    },
  });
};

/**
 * ดึง matter ตาม ID (พร้อม shareholders, directors, dockets, foreign_business_files)
 */
export const getIncorporateMatterById = (id) => {
  return httpClientInCorporate.get(`${server.INCORPORATE_MATTER}/${id}`);
};

/**
 * สร้าง matter ใหม่ (ส่งเป็น FormData เพราะมีไฟล์แนบ)
 * @param {FormData} formData
 */
export const createIncorporateMatter = (formData) => {
  return httpClientInCorporate.post(`${server.INCORPORATE_MATTER}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

/**
 * แก้ไข matter (ส่งเป็น FormData)
 */
export const updateIncorporateMatter = (id, formData) => {
  return httpClientInCorporate.put(
    `${server.INCORPORATE_MATTER}/${id}`,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    },
  );
};

/**
 * ลบ matter
 */
export const deleteIncorporateMatter = (id) => {
  return httpClientInCorporate.delete(`${server.INCORPORATE_MATTER}/${id}`);
};

/**
 * ⭐ ลบไฟล์ Foreign Business เดี่ยว (license หรือ certificate)
 * ทันที ไม่ต้องรอ Save ทั้งฟอร์ม
 * @param {number|string} matterId
 * @param {number|string} fileId  id ของแถวใน incorporate_matter_foreign_business_file
 */
export const deleteIncorporateMatterForeignBusinessFile = (
  matterId,
  fileId,
) => {
  return httpClientInCorporate.delete(
    `${server.INCORPORATE_MATTER}/${matterId}/foreign-business-file/${fileId}`,
  );
};

// ============================================================
// INCORPORATE DOCKET
// ============================================================

/**
 * ดึง docket ทั้งหมด
 */
export const getAllIncorporateDocket = () => {
  return httpClientInCorporate.get(`${server.INCORPORATE_DOCKET}`);
};

/**
 * ดึง docket แบบ pagination + search + filter
 * @param {number} page
 * @param {number} limit
 * @param {string} sortBy
 * @param {string} sortOrder
 * @param {string|number} company_id
 * @param {string} search       ค้นหาจาก client_name, inquiry_topic, doc_category
 * @param {string} status       In Progress | Complete | Request Payment | Cancelled
 * @param {string} doc_category Company Incorporation Documents | List of Shareholders (BorJor.5) | Minutes of Board Meeting | Financial Statement Forms | Others
 */
export const getIncorporateDocketPagination = (
  page,
  limit,
  sortBy,
  sortOrder,
  company_id,
  search,
  status,
  doc_category,
) => {
  return httpClientInCorporate.get(`${server.INCORPORATE_DOCKET_PAGINATION}`, {
    params: {
      page,
      limit,
      sortBy,
      sortOrder,
      company_id,
      search,
      status,
      doc_category,
    },
  });
};

/**
 * ดึง docket ทั้งหมดที่ผูกกับ matter_id
 */
export const getIncorporateDocketByMatterId = (matter_id) => {
  return httpClientInCorporate.get(
    `${server.INCORPORATE_DOCKET_BY_MATTER}/${matter_id}`,
  );
};

/**
 * ดึง docket ตาม ID
 * ⭐ response.data จะมี field `changes` ติดมาด้วย (array ของ
 * incorporate_docket_change พร้อม `agencies` ของแต่ละรายการ)
 * เพราะ backend findById ทำ include ไว้ให้แล้ว
 */
export const getIncorporateDocketById = (id) => {
  return httpClientInCorporate.get(`${server.INCORPORATE_DOCKET}/${id}`);
};

/**
 * สร้าง docket ใหม่ (ส่งเป็น FormData เพราะอาจมีไฟล์แนบ)
 */
export const createIncorporateDocket = (formData) => {
  return httpClientInCorporate.post(`${server.INCORPORATE_DOCKET}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

/**
 * แก้ไข docket
 */
export const updateIncorporateDocket = (id, formData) => {
  return httpClientInCorporate.put(
    `${server.INCORPORATE_DOCKET}/${id}`,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    },
  );
};

/**
 * ลบ docket
 */
export const deleteIncorporateDocket = (id) => {
  return httpClientInCorporate.delete(`${server.INCORPORATE_DOCKET}/${id}`);
};

/**
 * ลบไฟล์แนบ 1 ไฟล์ออกจาก docket (ไม่ใช่ลบทั้ง docket)
 */
export const removeIncorporateDocketAttachment = (docket_id, filename) => {
  return httpClientInCorporate.delete(
    `${server.INCORPORATE_DOCKET}/${docket_id}/attachment`,
    { data: { filename } },
  );
};

// ============================================================
// ⭐ INCORPORATE DOCKET CHANGE (Change Tracker)
// ============================================================

/**
 * ดึง Change Tracker ทั้งหมดของ docket หนึ่งใบ (พร้อม agencies)
 */
export const getIncorporateDocketChangeByDocketId = (docket_id) => {
  return httpClientInCorporate.get(
    `${server.INCORPORATE_DOCKET_CHANGE_BY_DOCKET}/${docket_id}`,
  );
};

/**
 * ดึง change 1 รายการ ตาม ID (พร้อม agencies)
 */
export const getIncorporateDocketChangeById = (id) => {
  return httpClientInCorporate.get(`${server.INCORPORATE_DOCKET_CHANGE}/${id}`);
};

/**
 * สร้าง change 1 รายการ
 * @param {Object} data { docket_id, change_type, change_detail, agencies: [...] }
 */
export const createIncorporateDocketChange = (data) => {
  return httpClientInCorporate.post(
    `${server.INCORPORATE_DOCKET_CHANGE}`,
    data,
  );
};

/**
 * บันทึก Change Tracker ทั้งชุดของ docket หนึ่งใบ (ลบของเดิมทั้งหมด
 * แล้วสร้างใหม่ทั้งชุดในครั้งเดียว — ใช้จากหน้า Docket Add/Edit ตอนกด Save)
 * @param {number|string} docket_id
 * @param {Object} payload { changes: [ { change_type, change_detail, agencies }, ... ], user_id }
 */
export const replaceIncorporateDocketChangeAll = (docket_id, payload) => {
  return httpClientInCorporate.put(
    `${server.INCORPORATE_DOCKET_CHANGE_REPLACE_ALL}/${docket_id}`,
    payload,
  );
};

/**
 * แก้ไข change 1 รายการ
 */
export const updateIncorporateDocketChange = (id, data) => {
  return httpClientInCorporate.put(
    `${server.INCORPORATE_DOCKET_CHANGE}/${id}`,
    data,
  );
};

/**
 * ลบ change 1 รายการ (agencies ที่ผูกไว้จะถูกลบตามด้วย CASCADE)
 */
export const deleteIncorporateDocketChange = (id) => {
  return httpClientInCorporate.delete(
    `${server.INCORPORATE_DOCKET_CHANGE}/${id}`,
  );
};

/**
 * เพิ่มหน่วยงานเข้า change รายการหนึ่ง
 * @param {number|string} docket_change_id
 * @param {string} agency_name
 */
export const addIncorporateDocketChangeAgency = (
  docket_change_id,
  agency_name,
) => {
  return httpClientInCorporate.post(
    `${server.INCORPORATE_DOCKET_CHANGE}/${docket_change_id}/agency`,
    { agency_name },
  );
};

/**
 * ลบหน่วยงานออกจาก change รายการหนึ่ง
 */
export const removeIncorporateDocketChangeAgency = (
  docket_change_id,
  agency_name,
) => {
  return httpClientInCorporate.delete(
    `${server.INCORPORATE_DOCKET_CHANGE}/${docket_change_id}/agency`,
    { data: { agency_name } },
  );
};

/**
 * แก้สถานะการยื่นของหน่วยงานหนึ่ง (Pending / In Progress / Done)
 * @param {number|string} agency_id
 * @param {Object} data { status, filed_date, remark }
 */
export const updateIncorporateDocketChangeAgencyStatus = (agency_id, data) => {
  return httpClientInCorporate.put(
    `${server.INCORPORATE_DOCKET_CHANGE_AGENCY}/${agency_id}/status`,
    data,
  );
};

// ============================================================
// ⭐ INCORPORATE OVERVIEW — สรุปสถานะงาน Docket ทั้งหมด
// ============================================================

/**
 * ดึงข้อมูลสรุปสำหรับหน้า Overview ทั้งหมดในครั้งเดียว
 * (stats, by_doc_category, by_agency, urgent_dockets)
 * @param {string|number} company_id
 * @param {number} due_within_days  จำนวนวันสำหรับนับ "ใกล้ครบกำหนด" (default 30)
 */
export const getIncorporateOverview = (company_id, due_within_days = 30) => {
  return httpClientInCorporate.get(`${server.INCORPORATE_DOCKET_OVERVIEW}`, {
    params: { company_id, due_within_days },
  });
};
